import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPriceAlertConfigs,
  createPriceAlertConfig,
  updatePriceAlertConfig,
  deletePriceAlertConfig,
  getPriceAlertEvents,
  getPriceAlertEventsBySymbol,
  getPriceAlertStats,
  PriceAlertEventsPage,
  CreatePriceAlertConfigPayload,
  UpdatePriceAlertConfigPayload,
} from "@/api/price-alerts";

const QUERY_KEY_CONFIGS = "price-alert-configs";
const QUERY_KEY_EVENTS = "price-alert-events";
const QUERY_KEY_STATS = "price-alert-stats";
const QUERY_KEY_SYMBOL_HISTORY = "price-alert-symbol-history";

export const usePriceAlertStats = (userId: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEY_STATS, userId],
    queryFn: () => getPriceAlertStats(userId),
    enabled: enabled && !!userId,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
};

export const usePriceAlertSymbolHistory = (symbol: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEY_SYMBOL_HISTORY, symbol],
    queryFn: () => getPriceAlertEventsBySymbol(symbol),
    enabled: enabled && !!symbol,
    staleTime: 60_000,
  });
};

export const usePriceAlertConfigs = (userId: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEY_CONFIGS, userId],
    queryFn: () => getPriceAlertConfigs(userId),
    enabled: enabled && !!userId,
    staleTime: 30_000,
  });
};

export const useInfinitePriceAlertEvents = (
  userId: string,
  limit = 10,
  enabled = true,
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY_EVENTS, userId, limit],
    queryFn: ({ pageParam = 1 }): Promise<PriceAlertEventsPage> =>
      getPriceAlertEvents(userId, { limit, page: pageParam }),
    enabled: enabled && !!userId,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
};

export const useCreatePriceAlertConfig = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePriceAlertConfigPayload) =>
      createPriceAlertConfig(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CONFIGS, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_STATS, userId] });
    },
  });
};

export const useUpdatePriceAlertConfig = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      payload,
    }: {
      configId: string;
      payload: UpdatePriceAlertConfigPayload;
    }) => updatePriceAlertConfig(userId, configId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CONFIGS, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_STATS, userId] });
    },
  });
};

export const useDeletePriceAlertConfig = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) => deletePriceAlertConfig(userId, configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CONFIGS, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_STATS, userId] });
    },
  });
};
